import { Sprite, Assets, Text, TextStyle, Container } from 'pixi.js';
import CardAnimations from '../CardAnimations.js';
import { ANIMATION_DURATIONS } from '../../config/animationConfig.js';
import { toVisualSlot, toActualSeat, getDealingOrder } from './onlineSeat.js';
import ScoreMarkerRenderer from '../ScoreMarkerRenderer.js';
import OnlineDeckRenderer from './OnlineDeckRenderer.js';

/** Spanish deck size (same constant offline's Deck.js hardcodes) — the pile is shown full right before the first card is dealt off it. */
const FULL_DECK_SIZE = 40;

/**
 * Renders the online Báciga table — a sibling fork of OnlineTableRenderer.js
 * (itself a fork of the offline DealManagerRenderer.js — see that file's own
 * doc comment for the rationale), NOT shared code with either: Báciga has no
 * center pile (cards are dealt straight into hands, no drop/collect
 * mechanic) and, unlike Ronda, `BacigaPlayerView` never reveals another
 * seat's actual card suit/value at any point — not for a played card, not
 * for a declared seat's Las31 "reveal all", not for the dealer's torito
 * face-up card (RONDA_ONLINE.md Fase 12c: a deliberate, documented
 * simplification vs. offline parity, noted in TODO.md). Every opponent hand
 * is therefore always rendered as a set of anonymous face-down placeholders
 * — their *count* changes (Las31 hits add one) and one of them may get a
 * covered-card tint (`applyCoveredTint`, from `hasCoveredCard` — same
 * privacy tier as `count`, never reveals which specific card nor its
 * suit/value) — while my own hand is always rendered fully from `myHand`,
 * which the server already keeps authoritative and complete after every
 * action.
 *
 * What's ported near-verbatim from OnlineTableRenderer.js (same math, same
 * primitives): seat-rotation via onlineSeat.js, name-label placement, the
 * 3-card hand position math (Báciga hands start at 3, same as Ronda), card
 * texture loading (including the face-down null-data sentinel for
 * opponents), interactivity/dim/destroy helpers.
 */
class OnlineBacigaRenderer {
    /**
     * `spectator`: a read-only viewer owns no seat — `mySeat` is then only the
     * visual perspective (seat 0 at the bottom), and every seat, that one
     * included, is rendered like an opponent (isMine() is false for all).
     */
    constructor(app, mySeat, { spectator = false } = {}) {
        this.app = app;
        this.mySeat = mySeat;
        this.spectator = spectator;

        this.cardWidth = 100;
        this.cardHeight = 150;

        /** Keyed by VISUAL SLOT (0 = bottom = me, 1 = right, 2 = top, 3 = left) — not by real seat. */
        this.playerCardsContainers = {};
        this.playerNameTexts = {};
        this.scoreMarkerRenderer = null;
        this.deckRenderer = new OnlineDeckRenderer(app);
        /** TODO.md (Báciga card-pass animation parity) — a temporary discard pile for the voting phase's "paso" exchange, ported from offline DealManagerRenderer.js's centerCardsContainer. Báciga otherwise has no center pile (see class doc comment): this exists only between animateCardsPassed's discard step and animateCenterCardsToDeck sweeping it into the deck at the start of Las31. */
        this.centerCardsContainer = null;

        this.initialize();
    }

    get dealDuration() {
        return ANIMATION_DURATIONS.DEAL_DURATION;
    }

    get dealDelay() {
        return ANIMATION_DURATIONS.DEAL_DELAY;
    }

    /** Offline parity (DealManagerRenderer.setCardSize) — call before setScoreMarkers/dealInitial so the deck/score sub-renderers pick it up the first time they read this.cardWidth/cardHeight. */
    setCardSize(width, height) {
        this.cardWidth = width;
        this.cardHeight = height;
    }

    initialize() {
        for (let slot = 0; slot < 4; slot++) {
            const container = new Container();
            container.label = `BacigaSeatSlot${slot}Cards`;
            container.zIndex = 100;
            container.sortableChildren = true;
            this.app.stage.addChild(container);
            this.playerCardsContainers[slot] = container;
        }
        this.centerCardsContainer = new Container();
        this.centerCardsContainer.label = 'BacigaCenterCards';
        this.centerCardsContainer.zIndex = 150;
        this.app.stage.addChild(this.centerCardsContainer);
        this.app.stage.sortableChildren = true;
    }

    // ─── Seat mapping ──────────────────────────────────────────────────

    visualSlot(actualSeat) {
        return toVisualSlot(actualSeat, this.mySeat);
    }

    // ─── Names (ported verbatim from OnlineTableRenderer) ───────────────

    /** `playersBySeat`: array of 4 `{name, isSystem, avatar?}`, indexed by REAL seat (0-3). */
    setPlayerNames(playersBySeat) {
        Object.values(this.playerNameTexts).forEach((item) => {
            if (item?.name && !item.name.destroyed) item.name.destroy();
            if (item?.avatar && !item.avatar.destroyed) item.avatar.destroy();
        });
        this.playerNameTexts = {};

        const screenWidth = this.app.screen.width;
        const screenHeight = this.app.screen.height;
        const centerX = screenWidth / 2;
        const centerY = screenHeight / 2;
        const margin = this.cardHeight * 0.6;

        const nameStyle = new TextStyle({
            fontFamily: 'Arial, sans-serif',
            fontSize: 16,
            fontWeight: 'bold',
            fill: 0xffffff,
            stroke: { color: 0x000000, width: 3 },
            dropShadow: { color: 0x000000, blur: 4, angle: Math.PI / 4, distance: 2 },
        });
        const avatarStyle = new TextStyle({ fontFamily: 'Arial, sans-serif', fontSize: 22 });
        const avatarOffset = 35;

        for (let slot = 0; slot < 4; slot++) {
            const actualSeat = toActualSeat(slot, this.mySeat);
            const player = playersBySeat[actualSeat] ?? { name: '', isSystem: false };

            const nameText = new Text({ text: player.name, style: nameStyle });
            nameText.anchor.set(0.5);
            nameText.zIndex = 1;

            const avatarText = new Text({ text: player.avatar || (player.isSystem ? '🤖' : '🙂'), style: avatarStyle });
            avatarText.anchor.set(0.5);
            avatarText.zIndex = 1;

            switch (slot) {
                case 0:
                    nameText.x = centerX;
                    nameText.y = screenHeight - margin + 12;
                    avatarText.x = centerX;
                    avatarText.y = screenHeight - margin - avatarOffset + 12;
                    break;
                case 1:
                    nameText.x = screenWidth - margin + 12;
                    nameText.y = centerY;
                    avatarText.x = screenWidth - margin - avatarOffset + 12;
                    avatarText.y = centerY;
                    nameText.rotation = -Math.PI / 2;
                    avatarText.rotation = -Math.PI / 2;
                    break;
                case 2:
                    nameText.x = centerX;
                    nameText.y = margin - 12;
                    avatarText.x = centerX;
                    avatarText.y = margin + avatarOffset - 12;
                    break;
                case 3:
                    nameText.x = margin - 12;
                    nameText.y = centerY;
                    avatarText.x = margin + avatarOffset - 12;
                    avatarText.y = centerY;
                    nameText.rotation = Math.PI / 2;
                    avatarText.rotation = Math.PI / 2;
                    break;
                default:
                    break;
            }

            this.app.stage.addChild(avatarText);
            this.app.stage.addChild(nameText);
            this.playerNameTexts[slot] = { name: nameText, avatar: avatarText };
        }
    }

    // ─── Score marker (same on-table 8/9-oros + comodín props offline uses) ──

    /** Mounts the same on-table score marker offline uses (src/game/ScoreMarkerRenderer.js), driven by the two ScoreMarker instances the screen owns. Called once, right after construction. */
    setScoreMarkers(team1Marker, team2Marker) {
        this.scoreMarkerRenderer = new ScoreMarkerRenderer(this.app, team1Marker, team2Marker);
        this.scoreMarkerRenderer.setCardSize(this.cardWidth, this.cardHeight);
        this.scoreMarkerRenderer.render();
    }

    /** Refreshes the marker's visuals after its ScoreMarker instances' scores changed. */
    updateScoreMarkers() {
        this.scoreMarkerRenderer?.update();
    }

    // ─── Deck pile (visual only — see OnlineDeckRenderer's doc comment) ──

    /** Repositions the deck pile next to `dealerSeat` and resizes its visible stack to `remaining` cards — call on every GAME_STATE_SYNC. */
    async updateDeck(dealerSeat, remaining) {
        this.deckRenderer.setCardSize(this.cardWidth, this.cardHeight);
        this.deckRenderer.setPositionByVisualSlot(this.visualSlot(dealerSeat), this.app.screen.width, this.app.screen.height);
        await this.deckRenderer.setCount(remaining);
    }

    // ─── Layout (same math as OnlineTableRenderer's hand rows/fan) ──────

    /** `visualSlot` here is already rotated so mine is always 0. `cardIndex` can exceed 2 (Las31 hits grow a hand past 3) — the row/fan math just keeps spacing outward, no fixed cap. */
    getPlayerCardPosition(visualSlot, cardIndex) {
        if (!this.app?.renderer || !this.app?.screen) return { x: 0, y: 0, rotation: 0 };
        const screenWidth = this.app.screen.width;
        const screenHeight = this.app.screen.height;
        const centerX = screenWidth / 2;
        const centerY = screenHeight / 2;

        const spacing = this.cardWidth * 0.4;
        const margin = this.cardHeight * 0.6;

        switch (visualSlot) {
            case 0: {
                const fanRadius = this.cardWidth * 4;
                const fanAngle = 0.25;
                const cardAngle = (cardIndex - 1) * fanAngle;
                const arcX = centerX + Math.sin(cardAngle) * fanRadius;
                const arcY = screenHeight - margin + (1 - Math.cos(cardAngle)) * this.cardHeight * 0.3;
                return { x: arcX, y: arcY, rotation: cardAngle };
            }
            case 1: {
                const totalHeight = 2 * spacing;
                const startY = centerY - totalHeight / 2;
                return { x: screenWidth - margin, y: startY + cardIndex * spacing, rotation: -Math.PI / 2 };
            }
            case 2: {
                const totalWidth = 2 * spacing;
                const startX = centerX + totalWidth / 2;
                return { x: startX - cardIndex * spacing, y: margin, rotation: Math.PI };
            }
            case 3: {
                const totalHeight = 2 * spacing;
                const startY = centerY + totalHeight / 2;
                return { x: margin, y: startY - cardIndex * spacing, rotation: Math.PI / 2 };
            }
            default:
                return { x: 0, y: 0, rotation: 0 };
        }
    }

    /** Where dealt cards fly out from — the deck pile's own position/rotation, wherever it's currently planted (dealInitial positions it at the dealer's seat first). */
    getDealOrigin() {
        return this.deckRenderer.getPosition();
    }

    /** Port of offline DealManagerRenderer.getBacigaDiscardPosition — a small random scatter around screen center. Deliberately NOT visualSlot-dependent (unlike getPlayerCardPosition): every viewer sees the same center pile regardless of seat rotation. */
    getDiscardPosition() {
        if (!this.app?.renderer || !this.app?.screen) return { x: 0, y: 0, rotation: 0 };
        const centerX = this.app.screen.width / 2;
        const centerY = this.app.screen.height / 2;
        return {
            x: centerX + (Math.random() - 0.5) * 20,
            y: centerY + (Math.random() - 0.5) * 20,
            rotation: (Math.random() - 0.5) * 0.3,
        };
    }

    /**
     * Port of offline DealManagerRenderer.getBaciga31Position, `playerIndex`
     * renamed `visualSlot` (the bottom/right/top/left geometry is identical —
     * only which real seat lands in each slot differs, already handled by
     * visualSlot()). Spreads a hand horizontally (bottom/top) or vertically
     * (left/right), wrapping into extra rows/columns once it outgrows the
     * screen, with `stackOffset` nudging same-value (ligues) cards into a
     * visible overlapping stack instead of hiding them on top of each other.
     */
    getLas31Position(visualSlot, cardIndex, totalCards = 3, stackOffset = 0) {
        if (!this.app?.renderer || !this.app?.screen) return { x: 0, y: 0, rotation: 0 };
        const screenWidth = this.app.screen.width;
        const screenHeight = this.app.screen.height;
        const centerX = screenWidth / 2;
        const centerY = screenHeight / 2;

        const spacing = this.cardWidth * 0.85;
        const margin = this.cardHeight * 0.8;
        const rowSpacing = this.cardHeight * 0.7;
        const stackOffsetAmount = this.cardHeight * 0.25;

        if (visualSlot === 0 || visualSlot === 2) {
            const maxCardsPerRow = Math.floor((screenWidth - margin * 2) / spacing);
            const rowIndex = Math.floor(cardIndex / maxCardsPerRow);
            const cardIndexInRow = cardIndex % maxCardsPerRow;
            const cardsInThisRow = Math.min(maxCardsPerRow, totalCards - rowIndex * maxCardsPerRow);
            const rowStartOffset = -((cardsInThisRow - 1) * spacing) / 2;

            if (visualSlot === 0) {
                return {
                    x: centerX + rowStartOffset + cardIndexInRow * spacing,
                    y: screenHeight - margin - rowIndex * rowSpacing - stackOffset * stackOffsetAmount,
                    rotation: 0,
                };
            }
            return {
                x: centerX - rowStartOffset - cardIndexInRow * spacing,
                y: margin + rowIndex * rowSpacing + stackOffset * stackOffsetAmount,
                rotation: Math.PI,
            };
        }

        // visualSlot 1 (right) or 3 (left)
        const maxCardsPerCol = Math.floor((screenHeight - margin * 2) / spacing);
        const colIndex = Math.floor(cardIndex / maxCardsPerCol);
        const cardIndexInCol = cardIndex % maxCardsPerCol;
        const cardsInThisCol = Math.min(maxCardsPerCol, totalCards - colIndex * maxCardsPerCol);
        const colStartOffset = -((cardsInThisCol - 1) * spacing) / 2;

        if (visualSlot === 1) {
            return {
                x: screenWidth - margin - colIndex * rowSpacing - stackOffset * stackOffsetAmount,
                y: centerY + colStartOffset + cardIndexInCol * spacing,
                rotation: -Math.PI / 2,
            };
        }
        return {
            x: margin + colIndex * rowSpacing + stackOffset * stackOffsetAmount,
            y: centerY - colStartOffset - cardIndexInCol * spacing,
            rotation: Math.PI / 2,
        };
    }

    // ─── Card sprites (ported verbatim) ─────────────────────────────────

    getCardImagePath(suit, value) {
        const suitMap = { oros: 1, copas: 2, espadas: 3, bastos: 4 };
        const suitCode = suitMap[suit];
        const valueCode = value.toString().padStart(2, '0');
        return `assets/images/deck/${suitCode}${valueCode}.png`;
    }

    getCardBackPath() {
        const cardBack = localStorage.getItem('cardBack') || '001';
        return `assets/images/deck/${cardBack}.png`;
    }

    /** Face-down placeholders (every opponent card, always — see class doc comment) get a null-data sentinel. */
    async createCardSprite(card, faceUp) {
        const imagePath = faceUp ? this.getCardImagePath(card.suit, card.value) : this.getCardBackPath();
        const texture = await Assets.load(imagePath);
        const sprite = new Sprite(texture);
        sprite.width = this.cardWidth;
        sprite.height = this.cardHeight;
        sprite.anchor.set(0.5);
        sprite.cardData = card;
        sprite.isFaceUp = faceUp;
        return sprite;
    }

    /**
     * TODO.md: `BacigaPlayerView.otherHands[seat].hasCoveredCard` (same
     * privacy tier as `count` — it never reveals suit/value, nor which of
     * the anonymous face-down cards it actually is) used to be sent by the
     * server but never rendered. Applies the exact same visual treatment
     * offline's DealManagerRenderer.markCardAsCovered gives the human's own
     * covered card (semi-transparent + blue tint) to one arbitrary sprite in
     * a rival's face-down hand — since every one of their cards already
     * looks identical (anonymous, no suit/value), it doesn't matter which
     * physical sprite gets it, only that exactly one does.
     */
    applyCoveredTint(sprite) {
        sprite.alpha = 0.7;
        sprite.tint = 0xccccff;
    }

    // ─── Dealing ───────────────────────────────────────────────────────

    /**
     * Animates a fresh hand's GAME_STATE_SYNC into the initial deal: my real
     * 3 cards face-up, opponents' hands as face-down placeholders. Always
     * animates exactly 3 per seat — TODO.md ("se reparten más cartas de 3
     * cartas por jugador") — `payload` isn't guaranteed to still show the
     * bare 3-card deal by the time this runs: if the new hand's mano is a
     * SYSTEM seat, bacigaEngine.ts can chain their whole vote, Las31 reveal,
     * and several hits into the SAME action's cascade before the one
     * deferred sync that triggers this, so `otherHands[seat].count` (or
     * `myHand.length`) could already be well past 3. Capping here is safe:
     * `handleActionEvent` already buffers (pendingRedealEventsRef) every
     * BACIGA_ACTION_EVENT that arrived while this redeal was pending and
     * replays them right after this returns — so whatever's beyond the
     * first 3 (a hit, a reveal...) gets its own correct, separately paced
     * animation from that replay, exactly as if it had happened live.
     * `payload` is the raw GAME_STATE_SYNC payload (BacigaPlayerView) — no
     * separate mapping needed. No center phase (Báciga has none).
     */
    async dealInitial(payload) {
        const { myHand, otherHands, dealerIndex } = payload;
        const INITIAL_HAND_SIZE = 3;

        // Plant the (already-shuffled) deck pile at the dealer's seat, full,
        // before pulling the first card off it — same as offline's
        // DeckRenderer.render() ahead of DealManagerRenderer.animateDealBacigaHand.
        this.deckRenderer.setCardSize(this.cardWidth, this.cardHeight);
        this.deckRenderer.setPositionByVisualSlot(this.visualSlot(dealerIndex), this.app.screen.width, this.app.screen.height);
        await this.deckRenderer.render(FULL_DECK_SIZE);
        const origin = this.getDealOrigin();

        const handSprites = { [this.mySeat]: [] };
        for (const card of myHand.slice(0, INITIAL_HAND_SIZE)) {
            handSprites[this.mySeat].push(await this.createCardSprite(card, true));
        }
        for (const [seatStr, info] of Object.entries(otherHands)) {
            const seat = Number(seatStr);
            handSprites[seat] = [];
            const count = Math.min(info.count, INITIAL_HAND_SIZE);
            for (let i = 0; i < count; i++) {
                const sprite = await this.createCardSprite({ suit: null, value: null }, false);
                // Only tint as covered here if the cover is itself within
                // this capped 3-card deal (info.count === count) — an
                // opponent already mid-Las31 by the time this payload
                // landed gets their real cover state from the replayed
                // 'cardToHide'/'dealt31' events instead, which know the
                // TRUE covered index (possibly beyond this cap) correctly.
                if (info.hasCoveredCard && info.count === count && i === count - 1) this.applyCoveredTint(sprite);
                handSprites[seat].push(sprite);
            }
        }

        const maxRounds = Math.max(...Object.values(handSprites).map((s) => s.length), 0);
        // Real dealing order (mano first, dealer last) — same rule offline's
        // animateDealBacigaHand follows via DealManager.getDealingOrder.
        // This used to always start from visual slot 0 (me), dealing to
        // myself first regardless of who actually goes first.
        const seatOrder = getDealingOrder(dealerIndex);
        for (let round = 0; round < maxRounds; round++) {
            for (const seat of seatOrder) {
                const sprite = handSprites[seat]?.[round];
                if (!sprite) continue;
                const visualSlot = this.visualSlot(seat);

                sprite.x = origin.x;
                sprite.y = origin.y;
                sprite.rotation = origin.rotation;
                this.playerCardsContainers[visualSlot].addChild(sprite);

                const targetPos = this.getPlayerCardPosition(visualSlot, round);
                await Promise.all([
                    CardAnimations.slideCard(sprite, targetPos, this.dealDuration),
                    this.animateRotation(sprite, targetPos.rotation, this.dealDuration),
                ]);
                this.deckRenderer.removeTopCard();
                await this.delay(this.dealDelay);
            }
        }
    }

    /**
     * Instantly snaps the whole table to whatever a GAME_STATE_SYNC says —
     * no deal animation. Used after a reconnection, or whenever the current
     * hand's card counts no longer match what's on screen (a new hand
     * dealt, a Las31 hit, a hide/reveal — Báciga's frequent small state
     * changes make a full instant rebuild simpler and more robust than
     * tracking every incremental diff, matching the same fallback pattern
     * OnlineTableRenderer.js already uses for reconnection).
     */
    /** True only for the seat this client actually plays — never for a spectator, whose `mySeat` is just a camera angle. */
    isMine(seat) {
        return !this.spectator && seat === this.mySeat;
    }

    async renderState(payload) {
        const { myHand, otherHands, phase } = payload;
        this.clearAllCards();
        const isLas31 = phase === 'playing31';

        for (let i = 0; i < myHand.length; i++) {
            const sprite = await this.createCardSprite(myHand[i], true);
            if (!isLas31) {
                const pos = this.getPlayerCardPosition(0, i); // visual slot 0 = me, always
                sprite.x = pos.x;
                sprite.y = pos.y;
                sprite.rotation = pos.rotation;
            }
            this.playerCardsContainers[0].addChild(sprite);
        }

        for (const [seatStr, info] of Object.entries(otherHands)) {
            const seat = Number(seatStr);
            const visualSlot = this.visualSlot(seat);
            const container = this.playerCardsContainers[visualSlot];
            // During Las31, once revealed, order sprites as [real cards...,
            // covered placeholder last if any] — a rebuild-from-scratch has
            // no existing sprite to keep aligned with the covered card's
            // TRUE original hand index (unlike the live event-driven reveal
            // path), and the value-grouped layout doesn't care about
            // original order anyway, only about which values cluster
            // together, so this is exactly as correct-looking.
            const revealedCards = isLas31 ? info.cards : null;
            for (let i = 0; i < info.count; i++) {
                const revealed = revealedCards?.[i];
                const sprite = await this.createCardSprite(revealed ?? { suit: null, value: null }, Boolean(revealed));
                if (info.hasCoveredCard && i === info.count - 1) this.applyCoveredTint(sprite);
                if (!isLas31) {
                    const pos = this.getPlayerCardPosition(visualSlot, i);
                    sprite.x = pos.x;
                    sprite.y = pos.y;
                    sprite.rotation = pos.rotation;
                }
                container.addChild(sprite);
            }
            // coveredIndex is deliberately null here (unlike the live
            // event-driven reveal path) — an opponent's covered slot is
            // already excluded from `info.cards` with nothing to align it
            // to a specific index, and this rebuild-from-scratch path (used
            // for a reconnect) has no way to know MY OWN covered index
            // either, since myHand carries no such marker — a minor,
            // reconnect-only gap (I'd just miss my own covered-card tint
            // until it next changes), same spirit as other already-accepted
            // "missed while disconnected" gaps in this codebase.
            if (isLas31) await this.repositionSeatForLas31(seat, revealedCards, null, { animate: false });
        }
        // A spectator's seat 0 came through otherHands above, already laid out from its revealed cards.
        if (isLas31 && !this.spectator) await this.repositionSeatForLas31(0, myHand, null, { animate: false });
    }

    // ─── Card-pass animations (TODO.md: Báciga voting-phase discard/redeal) ──

    /**
     * Animates the whole "everyone passed" exchange for every seat that
     * discarded at least one card: first every seat's discards slide face-
     * down to the center (in ascending seat order), THEN every seat gets its
     * replacement cards dealt back from the deck (also face-down — even for
     * me, since bacigaEngine.ts's 'cardsPassed' event only carries counts,
     * never card identity; my own new cards are revealed separately by
     * revealReplacementCards once the next GAME_STATE_SYNC's real myHand
     * arrives). `discardCounts` is `{seat: count}` exactly as broadcast
     * (seat keys arrive as strings over JSON). `myDiscardIndices` are MY OWN
     * hand indices I actually discarded (known locally — I sent them);
     * everyone else's cards are anonymous placeholders, so any `count` of
     * them work equally — the trailing ones are picked, which conveniently
     * also means the surviving sprites never need repositioning.
     * `dealerIndex` orders both passes the same way the backend's own
     * DealManager.dealReplacementCards() does (mano first, dealer last, via
     * getDealingOrder) — this used to just sort by ascending seat number,
     * out of step with the real deal order (same class of bug dealInitial()
     * had for the very first deal).
     */
    async animateCardsPassed(discardCounts, myDiscardIndices = [], dealerIndex = this.mySeat) {
        const countsBySeat = new Map(
            Object.entries(discardCounts).map(([seatStr, count]) => [Number(seatStr), count])
        );
        const entries = getDealingOrder(dealerIndex)
            .filter((seat) => (countsBySeat.get(seat) ?? 0) > 0)
            .map((seat) => [seat, countsBySeat.get(seat)]);
        if (entries.length === 0) return;

        // Computed ONCE, before animateDiscardForSeat below removes any
        // sprite — recomputing this per seat AFTER the discard step (as this
        // used to do, via a closure re-reading getPlayerCardSprites) would
        // read that seat's already-shrunk hand size, producing wrong (even
        // negative, Pixi-throwing) indices for the deal step that follows.
        const positionsBySeat = new Map(entries.map(([seat, count]) => {
            if (this.isMine(seat)) return [seat, [...myDiscardIndices].sort((a, b) => a - b)];
            const handSize = this.getPlayerCardSprites(seat).length;
            return [seat, Array.from({ length: count }, (_, i) => handSize - count + i)];
        }));

        // TEMPORARY diagnostic logging (TODO.md: "más de 3 cartas" regression
        // still unconfirmed) — remove once that's confirmed fixed. Logs the
        // pre-discard sprite count per seat alongside the positions computed
        // from it, so a bad (negative, or count-mismatched) index shows up
        // directly instead of only as a later "too many/too few cards".
        console.log(
            `[OnlineBacigaRenderer] animateCardsPassed discardCounts=${JSON.stringify(discardCounts)} myDiscardIndices=${JSON.stringify(myDiscardIndices)} positions=${JSON.stringify([...positionsBySeat.entries()])} spriteCountsBefore=${JSON.stringify(entries.map(([seat]) => [seat, this.getPlayerCardSprites(seat).length]))}`
        );

        for (const [seat] of entries) {
            await this.animateDiscardForSeat(seat, positionsBySeat.get(seat));
        }
        for (const [seat] of entries) {
            await this.animateDealReplacementsForSeat(seat, positionsBySeat.get(seat));
        }
    }

    /** Slides the sprites at `positions` (that seat's own hand indices) from their hand to the center pile, face-down, one at a time — mirrors offline DealManagerRenderer.animateBacigaDiscard. Removed in DESCENDING order so an earlier removal never shifts the container index of a position still queued. */
    async animateDiscardForSeat(actualSeat, positions) {
        const container = this.playerCardsContainers[this.visualSlot(actualSeat)];
        if (!container) return;
        for (const position of [...positions].sort((a, b) => b - a)) {
            const sprite = container.children[position];
            if (!sprite) continue;
            container.removeChild(sprite);
            this.centerCardsContainer.addChild(sprite);
            const targetPos = this.getDiscardPosition();
            await Promise.all([
                CardAnimations.slideCard(sprite, { x: targetPos.x, y: targetPos.y }, this.dealDuration),
                this.animateRotation(sprite, targetPos.rotation, this.dealDuration),
            ]);
        }
    }

    /** Deals one face-down placeholder per entry in `positions` from the deck into that seat's hand, inserted at that exact index — mirrors offline's animateDealReplacementCards/animateDealReplacementToPlayer. Inserted in ASCENDING order so each insertion shifts only the sprites still to its right, converging on the correct final order (relies on animateDiscardForSeat having already fully removed every discarded sprite first). */
    async animateDealReplacementsForSeat(actualSeat, positions) {
        const visualSlot = this.visualSlot(actualSeat);
        const container = this.playerCardsContainers[visualSlot];
        if (!container) return;
        const origin = this.getDealOrigin();
        for (const position of [...positions].sort((a, b) => a - b)) {
            const sprite = await this.createCardSprite({ suit: null, value: null }, false);
            sprite.x = origin.x;
            sprite.y = origin.y;
            sprite.rotation = origin.rotation;
            if (position >= container.children.length) container.addChild(sprite);
            else container.addChildAt(sprite, position);

            const targetPos = this.getPlayerCardPosition(visualSlot, position);
            await Promise.all([
                CardAnimations.slideCard(sprite, targetPos, this.dealDuration),
                this.animateRotation(sprite, targetPos.rotation, this.dealDuration),
            ]);
            this.deckRenderer.removeTopCard();
            await this.delay(this.dealDelay);
        }
    }

    /** Flips MY OWN hand's just-dealt (still face-down) replacement cards to their real face, now that the server has confirmed them via GAME_STATE_SYNC — called once, right after that sync's myHand is applied. `indices` is the same array animateCardsPassed dealt face-down for me. */
    async revealReplacementCards(indices, myHand) {
        const container = this.playerCardsContainers[0]; // visual slot 0 = me, always
        if (!container || !indices?.length) return;
        for (const index of indices) {
            const sprite = container.children[index];
            const card = myHand[index];
            if (!sprite || !card || sprite.isFaceUp) continue;
            const texture = await Assets.load(this.getCardImagePath(card.suit, card.value));
            await CardAnimations.flipCard(sprite, texture, sprite.rotation);
            sprite.cardData = card;
            sprite.isFaceUp = true;
        }
    }

    /**
     * Flips MY hand sprite at `index` face-down IN PLACE, as an immediate
     * preview of a card I've just picked to discard — offline parity
     * (Baciga.jsx's handleToggleCardSelection flips the real on-table sprite
     * the moment a discard is selected, well before the batch actually
     * resolves). Deliberately does NOT touch `sprite.cardData` — it still
     * holds the real card underneath, so revertDiscardPreview can always
     * flip it back correctly, however much later that turns out to be.
     * A no-op if already face-down (nothing to flip).
     */
    async previewDiscard(index) {
        const sprite = this.playerCardsContainers[0]?.children[index];
        if (!sprite || !sprite.isFaceUp) return;
        const backTexture = await Assets.load(this.getCardBackPath());
        await CardAnimations.flipCard(sprite, backTexture, sprite.rotation);
        sprite.isFaceUp = false;
    }

    /**
     * Reverts previewDiscard — flips MY hand sprite at `index` back to its
     * real face, read straight from the sprite's own (never-cleared)
     * cardData. Two callers: a local "Cancelar" click, and — more
     * importantly — bacigaEngine.ts's applyVote() clearing pendingDiscards
     * without ever calling dealReplacements() when a báciga/bacigote/cartas
     * declaration preempts the paso cycle before it completes (so the
     * 'cardsPassed' event this card was waiting for never arrives at all);
     * OnlineBacigaScreen.jsx detects that case via the same voting->playing31
     * phase transition already used for Phase31SummaryModal. A no-op if
     * already face-up.
     */
    async revertDiscardPreview(index) {
        const sprite = this.playerCardsContainers[0]?.children[index];
        if (!sprite || sprite.isFaceUp || !sprite.cardData) return;
        const frontTexture = await Assets.load(this.getCardImagePath(sprite.cardData.suit, sprite.cardData.value));
        await CardAnimations.flipCard(sprite, frontTexture, sprite.rotation);
        sprite.isFaceUp = true;
    }

    /**
     * Sweeps every remaining center-pile sprite into the deck — port of
     * offline DealManagerRenderer.animateCenterCardsToDeck, called at the
     * voting->playing31 transition (after the Phase31SummaryModal, before
     * Las31 itself starts), matching bacigaEngine.ts's
     * moveDiscardedCardsToBottomOfDeck() (via startLas31()) moving the same
     * cards to the BOTTOM of the draw pile logically — so visually they
     * should slide BEHIND the deck's own sprite, not in front of it.
     * centerCardsContainer's zIndex (150) sits above the deck's
     * (OnlineDeckRenderer's container, zIndex 50) for the voting phase's own
     * discard-preview animation, where that ordering is correct; lowered
     * below the deck's just for this sweep, then restored — the container
     * is empty again by the time this returns, ready for the next hand's
     * own discard pile.
     */
    async animateCenterCardsToDeck() {
        const centerCards = [...this.centerCardsContainer.children];
        if (centerCards.length === 0) return;
        const deckPos = this.getDealOrigin();
        const originalZIndex = this.centerCardsContainer.zIndex;
        this.centerCardsContainer.zIndex = originalZIndex - 110; // below the deck's zIndex (50)
        try {
            const animations = centerCards.map(async (sprite, idx) => {
                await this.delay(idx * 50);
                await Promise.all([
                    CardAnimations.slideCard(sprite, { x: deckPos.x, y: deckPos.y }, this.dealDuration),
                    this.animateRotation(sprite, deckPos.rotation, this.dealDuration),
                ]);
                if (sprite.parent === this.centerCardsContainer) this.centerCardsContainer.removeChild(sprite);
                if (!sprite.destroyed) sprite.destroy();
            });
            await Promise.all(animations);
        } finally {
            this.centerCardsContainer.zIndex = originalZIndex;
        }
    }

    // ─── Las31 layout (TODO.md: card-pass/Las31 animation parity) ────────

    /**
     * Groups & repositions every CURRENT sprite of `seat`'s hand for the
     * Las31 layout — port of offline's animateToLas31Position/
     * repositionPlayerCardsFor31 (same grouping rule: an unbound wildcard, a
     * still-face-down sprite, or the seat's own covered card each get their
     * own position, never stacked — offline's isHidden checks `isCovered`
     * for exactly this reason: a value visible only to me must not leak by
     * clustering with a rival's matching, already-public card; everything
     * else groups by assignedValue||value so ligues visibly overlap),
     * generalized to serve every moment offline calls separately. `cards` is
     * that seat's real, hand-index-aligned card list — `myHand` for me
     * (always known) or a revealed opponent's reconstructed list (see
     * OnlineBacigaScreen.jsx's alignLas31Cards); pass undefined for a
     * not-yet-revealed opponent, which — since every `cards?.[idx]` then
     * comes back undefined and an unknown card counts as "hidden" here —
     * naturally falls out to a plain ungrouped spread instead of needing a
     * separate code path. `coveredIndex` is only ever meaningful for
     * `seat === this.mySeat` (an opponent's covered slot is already `null`
     * in `cards`, nothing more to mark).
     */
    async repositionSeatForLas31(seat, cards, coveredIndex, { animate = true, duration } = {}) {
        const visualSlot = this.visualSlot(seat);
        const container = this.playerCardsContainers[visualSlot];
        const sprites = container?.children ?? [];
        if (sprites.length === 0) return;

        const valueGroups = new Map();
        sprites.forEach((sprite, idx) => {
            const card = cards?.[idx];
            const isWildcard = card && card.value === 7 && (card.suit === 'oros' || card.suit === 'copas');
            const isUnboundWildcard = isWildcard && !card.assignedValue;
            const isMyCovered = this.isMine(seat) && idx === coveredIndex;
            const isHidden = !card || !sprite.isFaceUp || isMyCovered;
            const key = isUnboundWildcard || isHidden ? `hidden_${idx}` : String(card.assignedValue || card.value);
            if (!valueGroups.has(key)) valueGroups.set(key, []);
            valueGroups.get(key).push(idx);
        });

        const totalPositions = valueGroups.size;
        const animations = [];
        let positionIndex = 0;
        for (const indices of valueGroups.values()) {
            indices.forEach((idx, stackOffset) => {
                const sprite = sprites[idx];
                const targetPos = this.getLas31Position(visualSlot, positionIndex, totalPositions, stackOffset);
                if (animate) {
                    animations.push(
                        Promise.all([
                            CardAnimations.slideCard(sprite, { x: targetPos.x, y: targetPos.y }, duration ?? this.dealDuration),
                            this.animateRotation(sprite, targetPos.rotation, duration ?? this.dealDuration),
                        ])
                    );
                } else {
                    sprite.x = targetPos.x;
                    sprite.y = targetPos.y;
                    sprite.rotation = targetPos.rotation;
                }
            });
            positionIndex++;
        }
        await Promise.all(animations);
    }

    /** Moves all 4 seats into the Las31 layout at once — called right after animateCenterCardsToDeck() at the voting->playing31 transition. Only my own cards are actually known yet (`myHand`, never covered at this exact moment — nobody's had a Las31 turn yet); every opponent still spreads ungrouped until their own reveal repositions them. */
    async animateAllSeatsToLas31(myHand) {
        await Promise.all([0, 1, 2, 3].map((seat) => this.repositionSeatForLas31(seat, this.isMine(seat) ? myHand : undefined, null)));
    }

    /**
     * Deals one face-down placeholder card into `seat`'s Las31 spread from
     * the deck, then regroups the whole hand — port of offline's
     * animateDealCardFor31 + repositionPlayerCardsFor31. Always face-down at
     * deal time regardless of seat (even mine): bacigaEngine.ts's `dealt31`
     * event never carries the card's identity, so — same as the card-pass
     * feature's replacement cards — whoever's turn it is finds out for real
     * only once the next GAME_STATE_SYNC confirms it (OnlineBacigaScreen.jsx
     * reveals it then, via revealSeatCards). `existingCards`/`coveredIndex`
     * describe that seat's hand BEFORE this new card (existingCards one
     * shorter than the post-deal sprite count) — passed straight to
     * repositionSeatForLas31 so any already-revealed ligues grouping (and my
     * own already-covered card, if any) survives the reflow; the brand new
     * last slot simply has no entry, which the same "no card = hidden" rule
     * already treats as its own ungrouped position.
     */
    async animateDealCardForLas31(seat, existingCards, coveredIndex) {
        const visualSlot = this.visualSlot(seat);
        const container = this.playerCardsContainers[visualSlot];
        if (!container) return;
        const newIndex = container.children.length;

        const sprite = await this.createCardSprite({ suit: null, value: null }, false);
        const origin = this.getDealOrigin();
        sprite.x = origin.x;
        sprite.y = origin.y;
        sprite.rotation = origin.rotation;
        container.addChild(sprite);

        const targetPos = this.getLas31Position(visualSlot, newIndex, newIndex + 1);
        await Promise.all([
            CardAnimations.slideCard(sprite, { x: targetPos.x, y: targetPos.y }, this.dealDuration),
            this.animateRotation(sprite, targetPos.rotation, this.dealDuration),
        ]);
        this.deckRenderer.removeTopCard();

        await this.repositionSeatForLas31(seat, existingCards, coveredIndex, { duration: 200 });
    }

    /**
     * Flips every currently face-down sprite in `seat`'s hand to its real
     * face from `cards` (hand-index-aligned; an opponent's covered slot is
     * `null` there — nothing to flip, stays a plain placeholder), then
     * regroups the hand now that real values are known — port of offline's
     * revealCards/revealCardsExceptOne combined into one pass (a `null`
     * entry already means "leave this one alone", so one method covers
     * both). `coveredIndex` is separate from `cards` specifically so MY OWN
     * covered card — always present in `cards` with its real value, since
     * the server never hides my own hand from me — still gets flipped face
     * up AND tinted (`applyCoveredTint`) instead of masked away like an
     * opponent's; any other slot just gets its tint cleared in case it was
     * previously covered and just got revealed.
     */
    async revealSeatCards(seat, cards, coveredIndex = null) {
        const visualSlot = this.visualSlot(seat);
        const container = this.playerCardsContainers[visualSlot];
        if (!container) return;
        const expectedCount = cards?.length ?? 0;
        if (container.children.length !== expectedCount) {
            // Self-heal: the server's hand (`cards`, sized off the
            // authoritative otherHands[seat].count/myHand) is always right —
            // bacigaSession.test.ts's full-round drives assert this on every
            // single sync. If this seat's sprite count ever drifted from
            // that (e.g. an earlier discard/redeal animation that didn't
            // fully land), silently revealing by index would leave whatever
            // sits past the mismatch permanently stuck face-down. Rebuilding
            // this one seat's hand from scratch — same fallback principle
            // renderState() already uses wholesale for a reconnect — is a
            // no-op in the normal case (counts already match) and always
            // correct when they don't.
            console.warn(`[OnlineBacigaRenderer] seat ${seat} sprite count (${container.children.length}) doesn't match its hand size (${expectedCount}) — rebuilding before reveal.`);
            container.removeChildren();
            for (const card of cards ?? []) {
                container.addChild(await this.createCardSprite(card ?? { suit: null, value: null }, Boolean(card)));
            }
        }
        const sprites = container.children;
        for (let idx = 0; idx < sprites.length; idx++) {
            const sprite = sprites[idx];
            const card = cards?.[idx];
            if (!card) continue; // an opponent's covered/not-yet-revealed slot — leave the placeholder alone
            if (!sprite.isFaceUp) {
                const texture = await Assets.load(this.getCardImagePath(card.suit, card.value));
                await CardAnimations.flipCard(sprite, texture, sprite.rotation);
            }
            sprite.cardData = card;
            sprite.isFaceUp = true;
            if (this.isMine(seat) && idx === coveredIndex) {
                this.applyCoveredTint(sprite);
                continue;
            }
            sprite.alpha = 1;
            sprite.tint = 0xffffff;
        }
        await this.repositionSeatForLas31(seat, cards, coveredIndex, { duration: 200 });
    }

    animateRotation(sprite, targetRotation, duration) {
        return new Promise((resolve) => {
            const start = performance.now();
            const startRotation = sprite.rotation;
            let diff = targetRotation - startRotation;
            while (diff > Math.PI) diff -= 2 * Math.PI;
            while (diff < -Math.PI) diff += 2 * Math.PI;

            const animate = (time) => {
                if (sprite.destroyed) return;
                const t = Math.min(1, (time - start) / duration);
                const easedT = t < 0.5 ? 2 * t * t : 1 - (-2 * t + 2) ** 2 / 2;
                sprite.rotation = startRotation + diff * easedT;
                if (t < 1) requestAnimationFrame(animate);
                else {
                    sprite.rotation = targetRotation;
                    resolve();
                }
            };
            requestAnimationFrame(animate);
        });
    }

    delay(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }

    // ─── Interactivity (ported verbatim) ──────────────────────────────

    makePlayerCardsInteractive(actualSeat, callback) {
        const container = this.playerCardsContainers[this.visualSlot(actualSeat)];
        if (!container) return;

        container.children.forEach((sprite) => {
            if (!sprite._originalY) {
                sprite._originalY = sprite.y;
                sprite._originalScale = sprite.scale.x;
            }
            sprite.eventMode = 'static';
            sprite.cursor = 'pointer';
            sprite.removeAllListeners();

            sprite.on('pointerdown', () => callback(sprite.cardData, sprite));
            sprite.on('pointerover', () => {
                sprite.zIndex = 1000;
                sprite.scale.set(sprite._originalScale * 1.2);
                sprite.y = sprite._originalY - 20;
            });
            sprite.on('pointerout', () => {
                sprite.zIndex = 0;
                sprite.scale.set(sprite._originalScale);
                sprite.y = sprite._originalY;
            });
        });
    }

    disablePlayerCardsInteractivity(actualSeat) {
        const container = this.playerCardsContainers[this.visualSlot(actualSeat)];
        if (!container) return;
        container.children.forEach((sprite) => {
            sprite.eventMode = 'none';
            sprite.cursor = 'default';
            sprite.removeAllListeners();
        });
    }

    /** Minimal visual cue for a disconnected/substituted seat — dims that seat's card pile. */
    setSeatDimmed(actualSeat, dimmed) {
        const container = this.playerCardsContainers[this.visualSlot(actualSeat)];
        if (container) container.alpha = dimmed ? 0.4 : 1;
    }

    // ─── Reads ─────────────────────────────────────────────────────────

    getPlayerCardSprites(actualSeat) {
        return this.playerCardsContainers[this.visualSlot(actualSeat)]?.children ?? [];
    }

    // ─── Cleanup ───────────────────────────────────────────────────────

    clearAllCards() {
        for (let slot = 0; slot < 4; slot++) {
            this.playerCardsContainers[slot].children.forEach((sprite) => sprite.destroy());
            this.playerCardsContainers[slot].removeChildren();
        }
        this.centerCardsContainer?.children.forEach((sprite) => sprite.destroy());
        this.centerCardsContainer?.removeChildren();
    }

    destroy() {
        CardAnimations.cancelAllAnimations();
        this.clearAllCards();
        this.deckRenderer.destroy();

        if (this.centerCardsContainer) {
            if (this.centerCardsContainer.parent === this.app.stage) this.app.stage.removeChild(this.centerCardsContainer);
            if (!this.centerCardsContainer.destroyed) this.centerCardsContainer.destroy();
            this.centerCardsContainer = null;
        }

        Object.values(this.playerNameTexts).forEach((item) => {
            if (item?.name) {
                if (item.name.parent === this.app.stage) this.app.stage.removeChild(item.name);
                if (!item.name.destroyed) item.name.destroy();
            }
            if (item?.avatar) {
                if (item.avatar.parent === this.app.stage) this.app.stage.removeChild(item.avatar);
                if (!item.avatar.destroyed) item.avatar.destroy();
            }
        });
        this.playerNameTexts = {};

        Object.values(this.playerCardsContainers).forEach((container) => {
            if (container.parent === this.app.stage) this.app.stage.removeChild(container);
            if (!container.destroyed) container.destroy();
        });
        this.playerCardsContainers = {};
    }
}

export default OnlineBacigaRenderer;
